import { ApiError, apiFetch, TOKEN_KEY, token } from "../api/client";

/**
 * SIGNING IN, against the real API.
 *
 * `POST /authentications/login` with `{ email, password }` answers
 * `{ status, message, data: { token } }`. The envelope is peeled by the HTTP
 * client — see `unwrap` in `api/client.ts` — so what lands here is `{ token }`.
 * The token goes to the client's store and every later call carries it — see
 * `api/client.ts`.
 *
 * ⚠️ THERE ARE TWO APIS, and picking the wrong one costs an afternoon. The one
 * V1 runs on — `api.dev.gayakoa.com`, port 3123 locally — serves
 * `/authentications/login` and holds the front-office settings at
 * `/domains/:domainId/events/:eventId/settings/fo`. The newer one, `apix…` on
 * port 5500, serves `/auth/login` and does not hold them. V2 needs the settings,
 * so V2 follows V1. Aiming at the wrong one answers a cheerful 404: "Oops, cette
 * route n'existe pas".
 *
 * `eventId` is NOT sent: the account is checked before any event is involved, and
 * V2 has no event context yet. Verified against the live dev API.
 *
 * ⚠️ THE SERVER'S OWN MESSAGES ARE NOT RELAYED. It answers "Le compte n'existe
 * pas" or "Le mot de passe est incorrect" depending on which failed, which turns
 * the sign-in form into a way to find out whether an address is registered for
 * this event — a list worth money to whoever asks patiently enough. The API's own
 * public route refuses to do that ("message volontairement identique dans les
 * deux cas : ne pas révéler quels emails existent"); the internal one still does,
 * so we stop it here. One message, whatever went wrong.
 *
 * Displayed strings are French: this is a French product.
 */

export type Session = {
  token: string;
  /** First sign-in: the server issues a short-lived token and a password is expected. */
  firstConnection: boolean;
};

/** What the form shows. Never the server's wording. */
export const SIGN_IN_FAILED = "Email ou mot de passe incorrect.";
export const SIGN_IN_UNAVAILABLE =
  "La connexion est momentanément indisponible. Réessayez dans un instant.";

export class SignInError extends Error {
  constructor(
    message: string,
    /** True when the credentials were refused, as opposed to the service failing. */
    readonly refused: boolean,
  ) {
    super(message);
    this.name = "SignInError";
  }
}

/**
 * Turns whatever went wrong into what the visitor should read.
 *
 * Pulled out and exported so the rule — REFUSED AND NOT-FOUND READ THE SAME —
 * is asserted by a test rather than trusted to survive the next edit.
 */
export function signInMessage(cause: unknown): SignInError {
  if (cause instanceof ApiError) {
    // 400 (a field missing), 401, and 403 — which is what this API answers for
    // "no such account". All of them are one thing to the visitor.
    const refused =
      cause.status === 400 || cause.status === 401 || cause.status === 403;
    return new SignInError(
      refused ? SIGN_IN_FAILED : SIGN_IN_UNAVAILABLE,
      refused,
    );
  }
  // Network down, CORS, DNS: not the visitor's credentials.
  return new SignInError(SIGN_IN_UNAVAILABLE, false);
}

export async function signIn(
  email: string,
  password: string,
  signal?: AbortSignal,
): Promise<Session> {
  let answer: { token?: string; firstConnection?: boolean };
  try {
    answer = await apiFetch("/authentications/login", {
      method: "POST",
      // The address is trimmed and lowered here: people type "  Jean@Ex.FR " and
      // an account is not found by a capital letter.
      body: { email: email.trim().toLowerCase(), password },
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError")
      throw cause;
    throw signInMessage(cause);
  }

  if (!answer?.token) {
    // A 200 with no token would otherwise leave the visitor "signed in" with
    // nothing to prove it, and every later call failing for no visible reason.
    throw new SignInError(SIGN_IN_UNAVAILABLE, false);
  }

  token.save(answer.token);
  announce();
  return { token: answer.token, firstConnection: !!answer.firstConnection };
}

/**
 * ═══ OUVRIR UNE SESSION SUR UN JETON DÉJÀ OBTENU ═══
 *
 * La connexion par code (`auth/otp.ts`) ne passe pas par `/authentications/login` :
 * c'est apix qui vérifie le code et rend le jeton. Le jeton est le même (mêmes
 * champs `email` + `authenticationId`, mêmes secrets des deux côtés, vérifié dans
 * `api/src/hooks/useAuth.js` et `api-v1/api/utils/Token.js`), donc la suite est la
 * même : on le range et on prévient l'interface, exactement comme `signIn`.
 */
export function openSession(bearer: string): void {
  token.save(bearer);
  announce();
}

export function signOut(): void {
  token.clear();
  announce();
}

export function isSignedIn(): boolean {
  return token.read() !== null;
}

/**
 * ═══ THE SESSION, AS SOMETHING THE INTERFACE CAN WATCH ═══
 *
 * Reading `isSignedIn()` while rendering is not enough: nothing re-renders when
 * the token goes, so the guard would only notice at the next navigation and a
 * signed-out visitor would keep looking at a screen that is no longer theirs.
 *
 * ⚠️ OTHER TABS COUNT TOO. `storage` fires in every OTHER tab of the same site,
 * so signing out here signs out there — which is the whole point on a shared
 * machine, where the reason someone signs out is that the next person is waiting.
 */
type Listener = () => void;
const listeners = new Set<Listener>();

function announce(): void {
  for (const listener of listeners) listener();
}

/**
 * ⚠️ LE JETON SEUL CHANGE LA SESSION, pas n'importe quelle écriture d'un autre onglet.
 * `storage` part pour CHAQUE clé : le panneau y range chaque réglage modifié
 * (`settings/store.ts`). Pris pour un changement de session, cela vidait le cache des
 * requêtes personnelles de tous les autres onglets, droits compris : un organisateur
 * perdait son droit le temps d'une relecture, repassait en vue visiteur sans rien
 * demander, et la confirmation écrivait alors les réponses de son parcours de réglage,
 * email compris (tâche #195). `null` est un `clear()` : le jeton part avec le reste.
 */
export function isSessionChange(e: Pick<StorageEvent, "key">): boolean {
  return e.key === null || e.key === TOKEN_KEY;
}

export function subscribeToSession(listener: Listener): () => void {
  listeners.add(listener);
  // `storage` never fires in the tab that wrote it — hence `announce()` above.
  const fromOtherTab = (e: StorageEvent) => {
    if (isSessionChange(e)) listener();
  };
  window.addEventListener("storage", fromOtherTab);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", fromOtherTab);
  };
}

/** Snapshot for `useSyncExternalStore`: a boolean, so it compares by value. */
export function sessionSnapshot(): boolean {
  return isSignedIn();
}

/**
 * ═══ QUI PARLE : L'EMPREINTE DE LA SESSION (bug 8, chasse du 2026-10-20) ═══
 *
 * ⚠️ UN BOOLÉEN « CONNECTÉ » NE DIT PAS QUI. Les requêtes personnelles étaient clefées sur
 * lui : A reste ouvert sur « Mes informations », un autre onglet ouvre `?token=<B>`, le jeton
 * du stockage devient celui de B — et la clef d'A n'a pas bougé. La purge de `main.tsx`
 * vidait le cache, la même clef se re-demandait, et « qui suis-je » répondait B pendant que
 * l'écran gardait les valeurs saisies par A en mémoire. L'enregistrement partait donc sous
 * l'identité de B (`PUT …/forms/data?userId=<B>`) avec le corps d'A, et la fiche de B en
 * était écrasée.
 *
 * L'empreinte est un condensé du jeton, jamais le jeton (une clef de cache se retrouve dans
 * les outils de débogage). Deux jetons différents, même pour la même personne reconnectée,
 * font deux sessions : le surcoût est une relecture, l'erreur contraire est une écriture
 * sous la mauvaise identité. Vide quand personne n'est connecté.
 */
export function fingerprint(bearer: string | null): string {
  if (!bearer) return "";
  // cyrb53 : non cryptographique, deux entiers de 32 bits, suffisant pour distinguer deux jetons.
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < bearer.length; i++) {
    const c = bearer.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return `s${(h2 >>> 0).toString(36)}${(h1 >>> 0).toString(36)}`;
}

/** L'empreinte de la session EN COURS, lue dans le stockage à l'instant de l'appel. */
export function sessionKey(): string {
  return fingerprint(token.read());
}

/**
 * La session est-elle toujours celle sous laquelle l'écran a été composé ?
 *
 * ⚠️ À POSER JUSTE AVANT TOUTE ÉCRITURE qui vise une personne (`me.id` dans l'adresse) :
 * entre le rendu et le clic, un autre onglet a pu changer de compte, et les valeurs
 * saisies appartiennent à l'ancien.
 */
export function sameSession(composedUnder: string): boolean {
  return sessionKey() === composedUnder;
}

/** L'écriture a été abandonnée : la session a changé depuis que l'écran a été composé. */
export class SessionChangedError extends Error {
  constructor() {
    super("session-changed");
    this.name = "SessionChangedError";
  }
}

/** Lève `SessionChangedError` si la session n'est plus celle de l'écran. */
export function assertSession(composedUnder: string): void {
  if (!sameSession(composedUnder)) throw new SessionChangedError();
}
